<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import IconBase from '@/components/icon/IconBase.vue'
import IconCamera from '@/components/icon/IconCamera.vue'
import IconFilm from '@/components/icon/IconFilm.vue'
import IconArrowRight from '@/components/icon/IconArrowRight.vue'
import IconBin from '@/components/icon/IconBin.vue'
import IconElipsisVertical from '@/components/icon/IconElipsisVertical.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import DropDownMenu from '@/components/Atoms/DropDownMenu.vue'
import DropDownMenuItem from '@/components/Atoms/DropDownMenuItem.vue'
import { dynamicPlayerSourceLabel } from '@/Utils/dynamicPlayerSource'

const router = useRouter()

const props = defineProps({
	player: {
		type: Object,
		required: true,
	},
	compactMode: {
		type: Boolean,
		default: false,
	},
})

const emit = defineEmits({
	'toggle': () => true,
	'delete': () => true,
})

// Source is derived the same way the detail page and the previous card did:
// a live-stream source uses a camera glyph, a VOD / live-to-VOD source uses film.
const hasSource = computed(() => !!(props.player?.source || props.player?.subSource))

const sourceIcon = computed(() => {
	if (props.player?.source && !props.player?.subSource) return IconCamera
	if (props.player?.subSource) return IconFilm
	return null
})

const sourceLabel = computed(() => dynamicPlayerSourceLabel(props.player, 'No source selected'))

// Name + human type of the assigned source, surfaced in the preview placeholder
// so the card conveys "what's behind this player" at a glance.
const sourceName = computed(() => dynamicPlayerSourceLabel(props.player))
const sourceTypeLabel = computed(() => {
	if (props.player?.source && !props.player?.subSource) return 'Live stream source'
	if (props.player?.subSource) return 'Video source'
	return ''
})

const detailLink = computed(() => `/dynamic/${props.player._id}`)

const navigate = (event) => {
	// Don't navigate when the click lands on an interactive control (menu,
	// rename input, buttons); mirrors StreamCardView's interaction guard.
	if (event) {
		const target = event.target
		const interactive = target.closest('button, a[href], input, textarea, select, [role="button"]')
		if (interactive) return
	}
	router.push(detailLink.value)
}

const onToggle = () => emit('toggle', props.player)

const onDelete = () => emit('delete', props.player)
</script>

<template>
	<!-- LIST VIEW -->
	<figure v-if="compactMode"
		class="flex flex-wrap md:flex-nowrap gap-x-3 gap-y-1 items-center w-full relative rounded-lg group bg-surface-2 transition-colors duration-200 hover:bg-surface-3 px-3 py-2 cursor-pointer m-0"
		@click="navigate($event)">
		<div class="relative shrink-0 overflow-hidden rounded h-12 w-24 bg-gradient-to-b from-surface-darker to-black flex items-center justify-center"
			:class="{ 'opacity-60': !player.enabled }">
			<icon-base :width="16" :height="16" :width-view-box="16" :height-view-box="14" class="opacity-70">
				<component :is="sourceIcon || IconFilm" />
			</icon-base>
		</div>
		<div class="flex min-w-0 flex-1 basis-0 md:basis-auto items-center gap-2">
			<div class="flex flex-col min-w-0 flex-1">
				<h3 :title="player.name" class="truncate text-sm md:text-base text-light-1 font-medium m-0 min-w-0">
					{{ player.name }}
				</h3>
				<p class="flex items-center gap-1.5 text-tiny m-0 min-w-0"
					:class="hasSource ? 'text-surface-8' : 'text-surface-6'">
					<icon-base v-if="sourceIcon" class="shrink-0" :width="12" :height="12" fill="none">
						<component :is="sourceIcon" />
					</icon-base>
					<span class="truncate" :title="sourceLabel">{{ sourceLabel }}</span>
				</p>
			</div>
			<div class="shrink-0 md:hidden" @click.stop>
				<DropDownMenu isIcon position="right" type="secondary" no-border>
					<template #toggle-button>
						<icon-base class="!ml-1">
							<IconElipsisVertical />
						</icon-base>
					</template>
					<template #menu-items>
						<DropDownMenuItem @click="onToggle">
							{{ player.enabled ? 'Disable' : 'Enable' }}
						</DropDownMenuItem>
						<DropDownMenuItem @click="onDelete">
							Delete
						</DropDownMenuItem>
					</template>
				</DropDownMenu>
			</div>
		</div>
		<div class="order-1 basis-full md:order-none md:basis-auto grow-0 shrink-0 flex items-center">
			<span
				class="inline-flex w-fit items-center gap-1.5 text-[11px] font-medium rounded-full px-2 py-0.5 bg-surface-4"
				:class="player.enabled ? 'text-success-light' : 'text-surface-7'">
				<span class="size-1.5 rounded-full" :class="player.enabled ? 'bg-success-light' : 'bg-surface-6'"></span>
				{{ player.enabled ? 'Enabled' : 'Disabled' }}
			</span>
		</div>
		<div class="hidden md:flex items-center gap-2 shrink-0" @click.stop>
			<FormButton isIcon type="danger" size="sm" aria-label="Delete" @click="onDelete">
				<icon-base>
					<icon-bin />
				</icon-base>
			</FormButton>
			<FormButton :type="player.enabled ? 'secondary' : 'main'" size="sm" @click="onToggle">
				{{ player.enabled ? 'Disable' : 'Enable' }}
			</FormButton>
		</div>
	</figure>

	<!-- GRID VIEW -->
	<figure v-else
		class="flex flex-col w-full bg-surface-2 h-full relative rounded-xl group shadow-[0_1px_3px_rgba(0,0,0,0.15),0_10px_28px_-8px_rgba(0,0,0,0.4)] transition-[box-shadow,transform] duration-200 ease-out hover:shadow-[0_2px_6px_rgba(0,0,0,0.18),0_20px_44px_-10px_rgba(0,0,0,0.5)] hover:-translate-y-0.5">
		<!-- Status affordance: a quiet status chip (a loud filled badge over-powers
		     the card since enabled/disabled isn't the primary signal). -->
		<div class="absolute !py-2.5 !px-3 z-20 w-full flex gap-x-2 items-center pointer-events-none">
			<span
				class="inline-flex items-center gap-1.5 text-[11px] font-medium rounded-full px-2 py-0.5 bg-black/45 backdrop-blur-sm"
				:class="player.enabled ? 'text-success-light' : 'text-surface-7'">
				<span class="size-1.5 rounded-full" :class="player.enabled ? 'bg-success-light' : 'bg-surface-6'"></span>
				{{ player.enabled ? 'Enabled' : 'Disabled' }}
			</span>
		</div>

		<!-- Preview area: neutral placeholder consistent with StreamThumb's no-preview state -->
		<div class="flex items-center justify-center bg-gradient-to-b from-surface-darker to-black relative shrink-0 overflow-hidden w-full aspect-video rounded-t-xl cursor-pointer"
			@click="navigate($event)">
			<div class="flex flex-col items-center justify-center gap-1.5 px-4 text-center text-surface-5 w-full min-w-0"
				:class="{ 'opacity-60': !player.enabled }">
				<icon-base :width="30" :height="30" :width-view-box="16" :height-view-box="14" class="opacity-70 shrink-0">
					<component :is="sourceIcon || IconFilm" />
				</icon-base>
				<template v-if="hasSource">
					<span v-if="sourceName" class="text-sm font-medium text-surface-8 truncate w-full" :title="sourceName">{{ sourceName }}</span>
					<span class="text-xs text-surface-6">{{ sourceTypeLabel }}</span>
				</template>
				<span v-else class="text-xs text-surface-6">No source assigned</span>
			</div>
			<div
				class="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
				<span
					class="inline-flex items-center gap-1.5 text-sm font-medium text-white bg-surface-3/90 rounded-full px-3.5 py-1.5 shadow-lg">
					Manage
					<icon-base :width="14" :height="14" :width-view-box="24" :height-view-box="24" fill="none">
						<icon-arrow-right />
					</icon-base>
				</span>
			</div>
		</div>

		<!-- Footer: title, then source meta sharing a row with the actions -->
		<figcaption class="flex flex-col gap-1.5 !p-4 flex-1 relative cursor-pointer"
			@click="navigate($event)">
			<h3 :title="player.name" class="truncate min-w-0 text-lg text-light-1">
				{{ player.name }}
			</h3>
			<div class="flex items-center gap-2 w-full mt-auto">
				<p class="flex items-center gap-2 text-sm text-surface-8 min-w-0">
					<icon-base v-if="sourceIcon" class="shrink-0 text-surface-7" fill="none">
						<component :is="sourceIcon" />
					</icon-base>
					<span class="truncate" :title="sourceLabel">{{ sourceLabel }}</span>
				</p>
				<div class="flex items-center gap-2 shrink-0 ml-auto">
					<FormButton isIcon type="danger" size="sm" aria-label="Delete" @click="onDelete"
						class="transition-opacity duration-200 lg:opacity-0 group-hover:opacity-100 focus:opacity-100">
						<icon-base>
							<icon-bin />
						</icon-base>
					</FormButton>
					<FormButton :type="player.enabled ? 'secondary' : 'main'" size="sm" @click="onToggle">
						{{ player.enabled ? 'Disable' : 'Enable' }}
					</FormButton>
				</div>
			</div>
		</figcaption>
	</figure>
</template>

<style scoped></style>
